import {
  FaSpotify,
  FaTiktok,
  FaExternalLinkAlt,
  FaMusic,
  FaLink,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { SiSoundcloud, SiBandcamp } from 'react-icons/si';

export const getSocialUrl = (platform, value) => {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  let clean = value.trim().replace(/^@/, '');
  clean = clean.replace(/^https?:\/\//i, '');
  clean = clean.replace(/^(www\.)?(instagram\.com|facebook\.com|youtube\.com|youtu\.be|tiktok\.com|x\.com|twitter\.com)\/?/i, '');
  switch (platform) {
    case 'instagram': return `https://instagram.com/${clean}`;
    case 'facebook': return `https://facebook.com/${clean}`;
    case 'youtube':
      if (value.startsWith('@')) return `https://youtube.com/@${clean}`;
      if (clean.startsWith('c/') || clean.startsWith('channel/') || clean.startsWith('user/')) return `https://youtube.com/${clean}`;
      return `https://youtube.com/@${clean}`;
    case 'twitter': return `https://x.com/${clean}`;
    case 'tiktok': return `https://tiktok.com/@${clean}`;
    default: return null;
  }
};

/** Detecta URL de Short (embed vertical 9:16). */
export const isYouTubeShort = (url) =>
  !!(url && /youtube\.com\/shorts\/|m\.youtube\.com\/shorts\//i.test(url));

export const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i,
    /m\.youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m?.[1]) return m[1];
  }
  return null;
};

export const linkIconMap = {
  spotify: FaSpotify,
  soundcloud: SiSoundcloud,
  bandcamp: SiBandcamp,
  tiktok: FaTiktok,
  twitter: FaXTwitter,
  shop: FaExternalLinkAlt,
  music: FaMusic,
  link: FaLink,
};

export function calendarioUtcDayStart(dateInput) {
  if (!dateInput) return null;
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Próximos primero (asc), sin fecha en medio, pasados al final (desc). */
export function sortMicrositeCalendarioItems(items, getFecha = (x) => x?.fecha) {
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const upcoming = [];
  const past = [];
  const noDate = [];
  for (const item of items || []) {
    const day = calendarioUtcDayStart(getFecha(item));
    if (day == null) {
      noDate.push(item);
      continue;
    }
    if (day >= todayUtc) upcoming.push(item);
    else past.push(item);
  }
  const byFechaAsc = (a, b) => {
    const fa = calendarioUtcDayStart(getFecha(a)) || 0;
    const fb = calendarioUtcDayStart(getFecha(b)) || 0;
    if (fa !== fb) return fa - fb;
    return String(a.horaInicio || '').localeCompare(String(b.horaInicio || ''));
  };
  upcoming.sort(byFechaAsc);
  past.sort((a, b) => -byFechaAsc(a, b));
  return [...upcoming, ...noDate, ...past];
}

export function isCalendarioItemPasado(item, getFecha = (x) => x?.fecha) {
  const day = calendarioUtcDayStart(getFecha(item));
  if (day == null) return false;
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return day < todayUtc;
}

/** Año mínimo (año anterior al actual, UTC) para pasados visibles en calendario web. */
export function calendarioAnoMinimoPasados(referenceDate = new Date()) {
  return referenceDate.getUTCFullYear() - 1;
}

/**
 * ¿Visible en listas de Tu Web / micrositio?
 * - Sin fecha (actuaciones): siempre visible.
 * - Hoy o futuro: siempre visible.
 * - Pasado: solo si `includePast` y el año del evento es el actual o el anterior.
 */
export function isCalendarioItemEnVentanaTuWeb(item, { includePast = false, getFecha = (x) => x?.fecha } = {}) {
  const fecha = getFecha(item);
  if (!fecha) return true;
  const day = calendarioUtcDayStart(fecha);
  if (day == null) return false;
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (day >= todayUtc) return true;
  if (!includePast) return false;
  const eventYear = new Date(fecha).getUTCFullYear();
  return eventYear >= calendarioAnoMinimoPasados(now);
}

/** Actuaciones visibles en Calendario (respeta `micrositeConfig.calendarioActuacionesOcultasIds`). */
export function micrositeCalendarioActuacionesVisibles(actuacionesPublicas, msConfig) {
  const hidden = new Set(Array.isArray(msConfig?.calendarioActuacionesOcultasIds) ? msConfig.calendarioActuacionesOcultasIds : []);
  const includePast = msConfig?.calendarioIncluirActuacionesPasadas === true;
  const visible = (actuacionesPublicas || []).filter(
    (a) => a && a.id && !hidden.has(a.id) && isCalendarioItemEnVentanaTuWeb(a, { includePast }),
  );
  return sortMicrositeCalendarioItems(visible);
}

/** Ensayos visibles en Calendario (respeta `micrositeConfig.calendarioEnsayosOcultosIds`). */
export function micrositeCalendarioEnsayosVisibles(ensayosPublicos, msConfig) {
  const hidden = new Set(Array.isArray(msConfig?.calendarioEnsayosOcultosIds) ? msConfig.calendarioEnsayosOcultosIds : []);
  const includePast = msConfig?.calendarioIncluirEnsayosPasados === true;
  const visible = (ensayosPublicos || []).filter(
    (e) => e && e.id && !hidden.has(e.id) && isCalendarioItemEnVentanaTuWeb(e, { includePast }),
  );
  return sortMicrositeCalendarioItems(visible);
}

export const FONT_OPTIONS = [
  { id: 'default', name: 'Por defecto', family: null, url: null },
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', Georgia, serif", url: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&display=swap' },
  { id: 'montserrat', name: 'Montserrat', family: "'Montserrat', sans-serif", url: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap' },
  { id: 'lora', name: 'Lora', family: "'Lora', Georgia, serif", url: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&display=swap' },
  { id: 'raleway', name: 'Raleway', family: "'Raleway', sans-serif", url: 'https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800&display=swap' },
  { id: 'cormorant', name: 'Cormorant Garamond', family: "'Cormorant Garamond', Georgia, serif", url: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap' },
  { id: 'poppins', name: 'Poppins', family: "'Poppins', sans-serif", url: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap' },
  { id: 'merriweather', name: 'Merriweather', family: "'Merriweather', Georgia, serif", url: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700;900&display=swap' },
  { id: 'oswald', name: 'Oswald', family: "'Oswald', sans-serif", url: 'https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;600;700&display=swap' },
  { id: 'dmserif', name: 'DM Serif Display', family: "'DM Serif Display', Georgia, serif", url: 'https://fonts.googleapis.com/css2?family=DM+Serif+Display&display=swap' },
  { id: 'worksans', name: 'Work Sans', family: "'Work Sans', sans-serif", url: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap' },
];

export const getFontById = (id) => FONT_OPTIONS.find(f => f.id === id) || FONT_OPTIONS[0];

export const tipoEventoLabels = {
  procesion: 'Procesion', concierto: 'Concierto', certamen: 'Certamen',
  ensayo_especial: 'Ensayo especial', otro: 'Otro evento',
};

/** IDs en `micrositeConfig.seccionesOcultas` ocultan bloque y enlace en la navegación del micrositio. */
export function isMicrositeSectionHidden(sectionId, msConfig) {
  const hidden = msConfig?.seccionesOcultas;
  if (!Array.isArray(hidden)) return false;
  return hidden.includes(sectionId);
}

/**
 * Catálogo para el editor de perfil (mostrar / ocultar y textos de ayuda).
 * `color` alinea el icono del menú lateral con el estilo de Gestión (Chakra colorScheme).
 */
export const MICROSITE_SECTION_CATALOG = [
  { id: 'nosotros', label: 'Sobre nosotros', desc: 'Biografía, especialidades (opcional en web) y plantilla de músicos.', color: 'blue' },
  { id: 'historia', label: 'Historia', desc: 'Relato largo de la trayectoria de la formación.', color: 'purple' },
  { id: 'noticias', label: 'Noticias', desc: 'Publicaciones breves con imagen opcional. Máx. 10 activas a la vez.', color: 'orange' },
  { id: 'directiva', label: 'Directiva', desc: 'Miembros y cargos que aparecen en tarjetas.', color: 'cyan' },
  { id: 'galeria', label: 'Galería', desc: 'Fotos públicas de tu perfil.', color: 'pink' },
  { id: 'videos', label: 'Vídeos', desc: 'Vídeos enlazados desde tu perfil.', color: 'red' },
  { id: 'repertorio', label: 'Repertorio', desc: 'Listado de obras del archivo musical.', color: 'orange' },
  { id: 'logros', label: 'Logros y premios', desc: 'Premios y reconocimientos destacados.', color: 'yellow' },
  { id: 'resenas', label: 'Avales y reseñas', desc: 'Bloque de confianza y opiniones publicadas.', color: 'green' },
  { id: 'calendario', label: 'Calendario', desc: 'Actuaciones (Gestión) y ensayos visibles en tu web.', color: 'teal' },
  { id: 'patrocinadores', label: 'Patrocinadores', desc: 'Logos y enlaces de colaboradores.', color: 'gray' },
  { id: 'enlaces', label: 'Enlaces', desc: 'Enlaces extra (Spotify, tienda, etc.) en la zona de contacto.', color: 'blue' },
  { id: 'contacto', label: 'Contacto', desc: 'Qué datos mostrar, contacto, ubicación en texto, redes y mensaje de contratación.', color: 'purple' },
];

/** Compatibilidad: la sección 'eventos' pasa a llamarse 'calendario'. */
export function normalizeMicrositeSectionId(id) {
  if (id === 'eventos') return 'calendario';
  return id;
}

/** Orden por defecto del cuerpo del micrositio (sin hero `inicio`). Misma secuencia que el catálogo del editor. */
export const MICROSITE_BODY_SECTION_IDS = MICROSITE_SECTION_CATALOG.map((c) => c.id);

/**
 * Orden efectivo de secciones del cuerpo a partir de `micrositeConfig.seccionesOrden` y el catálogo.
 * Si falta id o hay ids desconocidos, se completan al final en orden de catálogo.
 */
export function getMicrositeBodySectionOrder(msConfig) {
  const catalogIds = MICROSITE_BODY_SECTION_IDS;
  const raw = msConfig?.seccionesOrden;
  const fromUser = Array.isArray(raw)
    ? raw
        .filter((id) => typeof id === 'string')
        .map((id) => normalizeMicrositeSectionId(id))
        .filter((id) => catalogIds.includes(id))
    : [];
  const seen = new Set(fromUser);
  const tail = catalogIds.filter((id) => !seen.has(id));
  return [...fromUser, ...tail];
}

/** Texto mostrado en el micrositio cuando la sección está visible pero aún no hay contenido que mostrar. */
/** Qué líneas del bloque Contacto del micrositio se muestran (por defecto todo visible si hay datos). */
const CONTACTO_MOSTRAR_KEYS = ['email', 'telefono', 'website', 'ubicacion', 'redes', 'textoContratar'];

export function normalizeContactoMostrar(raw) {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const out = {};
  for (const k of CONTACTO_MOSTRAR_KEYS) {
    out[k] = src[k] === false ? false : true;
  }
  return out;
}

/**
 * True si, con los interruptores actuales, no queda nada que mostrar en la sección contacto
 * (salvo el título de sección).
 */
export function isMicrositeContactoSectionVisuallyEmpty(
  contacto,
  locationText,
  hasSocials,
  textoContratar,
  msConfig,
) {
  const cm = normalizeContactoMostrar(msConfig?.contactoMostrar);
  const tc = (textoContratar != null ? String(textoContratar) : '').trim();
  const showTexto = tc.length > 0 && cm.textoContratar;
  const showEmail = !!(contacto?.email && String(contacto.email).trim()) && cm.email;
  const showTel = !!(contacto?.telefono && String(contacto.telefono).trim()) && cm.telefono;
  const showWeb = !!(contacto?.website && String(contacto.website).trim()) && cm.website;
  const showLoc = !!(locationText && String(locationText).trim()) && cm.ubicacion;
  const showRedes = !!hasSocials && cm.redes;
  return !showTexto && !showEmail && !showTel && !showWeb && !showLoc && !showRedes;
}

export const MICROSITE_SECTION_EMPTY_HINT = {
  nosotros: 'Completa la biografía y las especialidades (Tu web → Contenido o Perfil público) para que aparezcan aquí.',
  historia: 'Escribe tu historia en Perfil → Tu web → Contenido.',
  noticias: 'Crea noticias en Perfil → Tu web → Contenido para que se muestren aquí.',
  directiva: 'Añade miembros de la directiva en Tu web → Contenido.',
  galeria: 'Sube fotos en la galería de tu perfil para mostrarlas en la web.',
  videos: 'Añade vídeos (enlaces de YouTube) en la sección de vídeos de tu perfil.',
  repertorio: 'Completa el repertorio en tu perfil para listarlo en la web.',
  logros: 'Añade logros y premios en Tu web → Contenido.',
  resenas: 'Aquí se mostrarán avales de cofradías y reseñas publicadas cuando las tengas.',
  calendario: 'Configura qué ensayos y actuaciones quieres mostrar en Tu web → Calendario.',
  patrocinadores: 'Añade patrocinadores en Tu web → Contenido.',
  enlaces: 'Configura enlaces (Spotify, tienda, etc.) en Tu web → Contenido.',
  contacto:
    'Activa las casillas de «Qué mostrar», rellena los datos aquí o en Perfil, y guarda el contenido de la web.',
};

/** Título de sección en micrositio (menú fijo + cabeceras del cuerpo); coincide con el catálogo del editor. */
export function getMicrositeSectionLabel(sectionId) {
  const item = MICROSITE_SECTION_CATALOG.find((c) => c.id === sectionId);
  return item?.label ?? sectionId;
}

/** Paleta por defecto de cada tema (vista previa en perfil y valores iniciales del micrositio). */
export const MICROSITE_THEME_DEFAULT_PALETTE = {
  moderno: { primario: '#7c3aed', secundario: '#1a202c', fondo: '#f7fafc' },
  clasico: { primario: '#2D3748', secundario: '#EDF2F7', fondo: '#FFFFFF' },
  elegante: { primario: '#1a202c', secundario: '#FFFFFF', fondo: '#E2E8F0' },
  /** Oscuro tipo landing SaaS: acento neón, paneles profundos. */
  aurora: { primario: '#38bdf8', secundario: '#020617', fondo: '#0f172a' },
  /** Revista / editorial: tierra sobre papel. */
  editorial: { primario: '#c2410c', secundario: '#1c1917', fondo: '#faf7f2' },
  /** Neo-brutalista: contraste duro y acento ácido. */
  pulso: { primario: '#facc15', secundario: '#000000', fondo: '#ffffff' },
  /** Gradientes líquidos y enfoque tech inmersivo. */
  vortex: { primario: '#22d3ee', secundario: '#0f172a', fondo: '#020617' },
  /** Cristal translúcido y capas suaves de luz. */
  glass: { primario: '#a78bfa', secundario: '#0b1020', fondo: '#eef2ff' },
  /** Alto contraste dinámico para scroll narrativo. */
  kinetic: { primario: '#fb7185', secundario: '#111827', fondo: '#f9fafb' },
};

/** Valores por defecto de animaciones al hacer scroll en el micrositio público. */
export const MICROSITE_ANIMACIONES_DEFAULT = {
  /** false = web estática (sin animaciones al scroll). */
  enabled: false,
  /** vertical | horizontal | opacidad */
  modo: 'opacidad',
  /** 0 = movimiento mínimo, 100 = desplazamiento muy marcado (y disparo del scroll más exigente). */
  intensidad: 0,
  /**
   * 0 = transición muy lenta, 50 = ritmo intermedio (compat. con duraciones previas), 100 = muy rápida.
   * Solo afecta la duración del tween, no la amplitud del movimiento.
   */
  rapidez: 50,
};

/**
 * Normaliza `micrositeConfig.animacionesMicrositio` desde API o formulario.
 * @param {unknown} input
 * @returns {{ enabled: boolean, modo: string, intensidad: number, rapidez: number }}
 */
export function normalizeAnimacionesMicrositio(input) {
  const o = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const modoRaw = typeof o.modo === 'string' ? o.modo : MICROSITE_ANIMACIONES_DEFAULT.modo;
  const modo = ['vertical', 'horizontal', 'opacidad'].includes(modoRaw) ? modoRaw : MICROSITE_ANIMACIONES_DEFAULT.modo;
  let intensidad = Number(o.intensidad);
  if (Number.isNaN(intensidad)) intensidad = MICROSITE_ANIMACIONES_DEFAULT.intensidad;
  intensidad = Math.min(100, Math.max(0, Math.round(intensidad)));
  let rapidez = Number(o.rapidez);
  if (Number.isNaN(rapidez)) rapidez = MICROSITE_ANIMACIONES_DEFAULT.rapidez;
  rapidez = Math.min(100, Math.max(0, Math.round(rapidez)));
  return {
    enabled: o.enabled === true,
    modo,
    intensidad,
    rapidez,
  };
}

/** Claves de visibilidad en el hero del micrositio (cabecera principal). Por defecto todo visible. */
export const MICROSITE_CABECERA_HERO_KEYS = [
  'mostrarVerificado',
  'mostrarUbicacion',
  'mostrarNMusicos',
  'mostrarAnoFundacion',
  'mostrarTipoBanda',
  'mostrarRrss',
];

/**
 * Normaliza `micrositeConfig.cabeceraHero` (booleans por campo del hero).
 * @param {unknown} input
 * @returns {Record<string, boolean>}
 */
export function normalizeCabeceraHero(input) {
  const o = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const out = {};
  for (const k of MICROSITE_CABECERA_HERO_KEYS) {
    out[k] = o[k] !== false;
  }
  return out;
}

/** Lee el toggle «plantilla de músicos en Sobre nosotros» desde JSON (boolean o valores sueltos del motor JSON). */
export function readMostrarPlantillaMusicosNosotros(mc) {
  if (!mc || typeof mc !== 'object' || Array.isArray(mc)) return false;
  const v = mc.mostrarPlantillaMusicosNosotros;
  if (v === true) return true;
  if (v === false || v == null) return false;
  if (v === 'true' || v === 1 || v === '1') return true;
  return false;
}

/** Modo de instrumentos en plantilla del micrositio: todos los asignados o uno por músico (afecta texto y orden). */
export function readPlantillaMusicosInstrumentosModo(mc) {
  if (!mc || typeof mc !== 'object' || Array.isArray(mc)) return 'todos';
  return mc.plantillaMusicosNosotrosInstrumentosModo === 'uno' ? 'uno' : 'todos';
}

/** Mapa músicoId → id de fila `instrumentosAsignados` elegida cuando el modo es `uno`. */
export function readPlantillaMusicosInstrumentoPorMusico(mc) {
  if (!mc || typeof mc !== 'object' || Array.isArray(mc)) return {};
  const raw = mc.plantillaMusicosNosotrosInstrumentoPorMusico;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, val] of Object.entries(raw)) {
    if (typeof k !== 'string' || typeof val !== 'string') continue;
    const kid = k.trim().slice(0, 48);
    const vid = val.trim();
    if (!kid || !/^[a-f0-9-]{36}$/i.test(vid)) continue;
    out[kid] = vid;
  }
  return out;
}

/** Toggle «especialidades en Sobre nosotros»; si no existe clave, por defecto true (mismo comportamiento que antes). */
export function readMostrarEspecialidadesNosotros(mc) {
  if (!mc || typeof mc !== 'object' || Array.isArray(mc)) return true;
  const v = mc.mostrarEspecialidadesNosotros;
  if (v === false) return false;
  if (v === true) return true;
  if (v == null) return true;
  if (v === 'false' || v === 0 || v === '0') return false;
  if (v === 'true' || v === 1 || v === '1') return true;
  return true;
}

/** Mapa de visibilidad del hero listo para usar en plantillas (valores por defecto = mostrar). */
export function getCabeceraHeroVisibilidad(msConfig) {
  return normalizeCabeceraHero(msConfig?.cabeceraHero);
}

/**
 * Colores efectivos para un tema concreto (`temaId` puede diferir del tema activo, p. ej. tarjetas del editor).
 * `colorPrimario` legacy solo aplica al tema **moderno** si no hay `coloresPorTema.moderno.primario` (no hereda a clásico/elegante).
 */
export function getMicrositePalette(msConfig, temaId) {
  const t = temaId || (msConfig && msConfig.tema) || 'moderno';
  const def = MICROSITE_THEME_DEFAULT_PALETTE[t] || MICROSITE_THEME_DEFAULT_PALETTE.moderno;
  const custom = (msConfig && msConfig.coloresPorTema && msConfig.coloresPorTema[t]) || {};
  let primario = custom.primario;
  if (!primario) {
    if (t === 'moderno' && msConfig?.colorPrimario) {
      const cp = String(msConfig.colorPrimario).trim().slice(0, 7);
      if (/^#[0-9A-Fa-f]{6}$/i.test(cp)) primario = cp;
    }
    if (!primario) primario = def.primario;
  }
  const secundario = custom.secundario || def.secundario;
  const fondo = custom.fondo || def.fondo;
  return { primario, secundario, fondo };
}

/** Imagen de fondo del hero del micrositio (no usa el fondo de la tarjeta del perfil público). */
export function getMicrositeHeroImageUrl(bandaData) {
  if (!bandaData) return null;
  return bandaData.imagenCabeceraWeb || bandaData.avatar || null;
}

/**
 * Enlaces del menú del micrositio: mismas secciones que el catálogo del editor, respetando `seccionesOcultas`.
 * Los argumentos extra se mantienen por compatibilidad con llamadas existentes.
 */
export const buildSections = (bandaData, msConfig, _avales, _eventosPublicos, _ensayosPublicos) => {
  if (!bandaData) return [];
  const s = [{ id: 'inicio', label: 'Inicio' }];
  for (const id of getMicrositeBodySectionOrder(msConfig)) {
    if (!isMicrositeSectionHidden(id, msConfig)) {
      s.push({ id, label: getMicrositeSectionLabel(id) });
    }
  }
  return s;
};

export const prepareData = (bandaData) => {
  const raw = bandaData?.micrositeConfig || {};
  const msConfig = {
    ...raw,
    contactoMostrar: normalizeContactoMostrar(raw.contactoMostrar),
    animacionesMicrositio: normalizeAnimacionesMicrositio(raw.animacionesMicrositio),
    cabeceraHero: normalizeCabeceraHero(raw.cabeceraHero),
    mostrarPlantillaMusicosNosotros: readMostrarPlantillaMusicosNosotros(raw),
    plantillaMusicosNosotrosInstrumentosModo: readPlantillaMusicosInstrumentosModo(raw),
    plantillaMusicosNosotrosInstrumentoPorMusico: readPlantillaMusicosInstrumentoPorMusico(raw),
    mostrarEspecialidadesNosotros: readMostrarEspecialidadesNosotros(raw),
    seccionesOrden: getMicrositeBodySectionOrder(raw),
    calendarioMostrarActuaciones: raw.calendarioMostrarActuaciones !== false,
    calendarioMostrarEnsayos: raw.calendarioMostrarEnsayos !== false,
    calendarioModo: raw.calendarioModo || 'lista',
    calendarioMeses: Array.isArray(raw.calendarioMeses) ? raw.calendarioMeses : [],
    calendarioActuacionesOcultasIds: Array.isArray(raw.calendarioActuacionesOcultasIds)
      ? raw.calendarioActuacionesOcultasIds
      : [],
    calendarioEnsayosOcultosIds: Array.isArray(raw.calendarioEnsayosOcultosIds)
      ? raw.calendarioEnsayosOcultosIds
      : [],
    calendarioIncluirActuacionesPasadas: raw.calendarioIncluirActuacionesPasadas === true,
    calendarioIncluirEnsayosPasados: raw.calendarioIncluirEnsayosPasados === true,
  };
  const localizacion = bandaData?.localizacion || {};
  const contacto = bandaData?.contactoPublico || {};
  const social = bandaData?.redesSociales || {};
  const ensayosPublicosRaw = Array.isArray(bandaData?.ensayosPublicos) && bandaData.ensayosPublicos.length > 0
    ? bandaData.ensayosPublicos
    : (bandaData?.ensayos || []).filter(e => e.publico);
  const ensayosPublicos = ensayosPublicosRaw.map((e) => {
    // Ensayos (Gestión): normalizamos a la misma forma que usaban los themes (diaSemana, horaInicio, horaFin, ubicacion)
    if (e && e.fecha) {
      const d = new Date(e.fecha);
      const diaSemana = d.toLocaleDateString('es-ES', { weekday: 'long' });
      return {
        id: e.id,
        fecha: e.fecha,
        diaSemana: (diaSemana || '').charAt(0).toUpperCase() + (diaSemana || '').slice(1),
        horaInicio: e.horaInicio,
        horaFin: e.horaFin,
        ubicacion: e.lugar,
        descripcion: e.descripcion,
        grupo: e.grupo,
      };
    }
    return e;
  });
  const eventosPublicos = bandaData?.eventosPublicos || [];
  const actuacionesPublicas = bandaData?.actuacionesPublicas || [];
  const locationText = [localizacion.ciudad, localizacion.provincia].filter(Boolean).join(', ');
  const hasSocials = !!(social.instagram || social.facebook || social.youtube || social.twitter);
  const tema = msConfig.tema || 'moderno';
  const pal = getMicrositePalette(msConfig, tema);
  const primaryColor = pal.primario;
  const secondaryColor = pal.secundario;
  const surfaceColor = pal.fondo;

  return {
    msConfig,
    localizacion,
    contacto,
    social,
    ensayosPublicos,
    eventosPublicos,
    actuacionesPublicas,
    locationText,
    hasSocials,
    primaryColor,
    secondaryColor,
    surfaceColor,
  };
};
